import React from 'react';
import { formatRelativeTime as formatRelativeTimeShared, activeTimezone } from '../../../utils/formatDateTime';
import { FileActivity, RecentActivityFeedProps } from '../../../types/dashboard';
import { Card, CardHeader, CardBody } from '../../shared/Card';
import { StatusBadge, StatusBadgeVariant } from '../../shared/StatusBadge';
import { useTimezone } from '../../../contexts/TimezoneContext';

// THUB-272 / THUB-186: delegate to the single shared, timezone-aware formatter.
// These replace local copies that parsed the backend's (formerly naive) timestamps as
// LOCAL time -- which future-dated every value and made every recent row render
// "Just now". Module-level shims rather than the useTimezone() hook because these are
// called from several sub-components and inside .map callbacks in this file.
function formatRelativeTime(value: string | null | undefined): string {
  return formatRelativeTimeShared(value, activeTimezone());
}

/**
 * Icon components for different event types
 */
const FileReadyIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const FileDeliveredIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
  </svg>
);

const FilePickedUpIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
  </svg>
);

const FileFailedIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const ProcessingIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

/**
 * Get the appropriate icon component for an event type
 */
const getEventIcon = (eventType: FileActivity['eventType']): React.FC<{ className?: string }> => {
  switch (eventType) {
    case 'FILE_READY':
      return FileReadyIcon;
    case 'FILE_DELIVERED':
      return FileDeliveredIcon;
    case 'FILE_PICKED_UP':
      return FilePickedUpIcon;
    case 'FILE_FAILED':
      return FileFailedIcon;
    case 'PROCESSING':
      return ProcessingIcon;
    default:
      return FileReadyIcon;
  }
};

/**
 * Get icon color classes based on event type
 */
const getIconColorClasses = (eventType: FileActivity['eventType']): string => {
  switch (eventType) {
    case 'FILE_READY':
      return 'text-success-500 dark:text-success-400';
    case 'FILE_DELIVERED':
      return 'text-primary-500 dark:text-primary-400';
    case 'FILE_PICKED_UP':
      return 'text-primary-400 dark:text-primary-300';
    case 'FILE_FAILED':
      return 'text-danger-500 dark:text-danger-400';
    case 'PROCESSING':
      return 'text-warning-500 dark:text-warning-400';
    default:
      return 'text-neutral-500 dark:text-neutral-400';
  }
};

/**
 * Get icon background color classes based on event type
 */
const getIconBgClasses = (eventType: FileActivity['eventType']): string => {
  switch (eventType) {
    case 'FILE_READY':
      return 'bg-success-100 dark:bg-success-900/30';
    case 'FILE_DELIVERED':
      return 'bg-primary-100 dark:bg-primary-900/30';
    case 'FILE_PICKED_UP':
      return 'bg-primary-100 dark:bg-primary-900/30';
    case 'FILE_FAILED':
      return 'bg-danger-100 dark:bg-danger-900/30';
    case 'PROCESSING':
      return 'bg-warning-100 dark:bg-warning-900/30';
    default:
      return 'bg-neutral-100 dark:bg-neutral-800';
  }
};

/**
 * Map activity status to StatusBadge variant
 */
const getStatusBadgeVariant = (status: FileActivity['status']): StatusBadgeVariant => {
  switch (status) {
    case 'success':
      return 'success';
    case 'failed':
      return 'danger';
    case 'processing':
      return 'processing';
    case 'pending':
      return 'warning';
    default:
      return 'neutral';
  }
};

/**
 * Get display label for status
 */
const getStatusLabel = (status: FileActivity['status']): string => {
  switch (status) {
    case 'success':
      return 'Success';
    case 'failed':
      return 'Failed';
    case 'processing':
      return 'Processing';
    case 'pending':
      return 'Pending';
    default:
      return status;
  }
};

/**
 * Format timestamp to relative time (e.g., "2 minutes ago")
 */
/**
 * Truncate filename if too long
 */
const truncateFilename = (filename: string, maxLength: number = 30): string => {
  // THUB-291: this line was the actual throw site of the "Content Error" seen in NP and
  // reproduced in DEV -- `filename.length` where filename was undefined. The upstream cause
  // (an unchecked `as FileProcessingPayload` cast in DashboardOverview) is fixed, but this
  // guard stays: a display helper must never be able to blank the whole page. Note the
  // parameter is typed `string`, which is precisely why the compiler could not save us -- the
  // value arrived through a cast rather than a checked boundary.
  if (typeof filename !== 'string' || !filename) return '—';
  if (filename.length <= maxLength) return filename;

  const extension = filename.lastIndexOf('.') > 0
    ? filename.substring(filename.lastIndexOf('.'))
    : '';
  const nameWithoutExt = filename.substring(0, filename.length - extension.length);
  const truncatedName = nameWithoutExt.substring(0, maxLength - extension.length - 3);

  return `${truncatedName}...${extension}`;
};

/**
 * Skeleton loader for activity items
 */
const ActivityItemSkeleton: React.FC = () => (
  <div className="flex items-start gap-3 p-3 animate-pulse">
    {/* Icon skeleton */}
    <div className="flex-shrink-0 w-9 h-9 rounded-full bg-neutral-200 dark:bg-neutral-700" />

    {/* Content skeleton */}
    <div className="flex-1 min-w-0 space-y-2">
      <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded w-3/4" />
      <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded w-1/2" />
    </div>

    {/* Status skeleton */}
    <div className="flex-shrink-0 h-6 w-16 bg-neutral-200 dark:bg-neutral-700 rounded-full" />
  </div>
);

/**
 * Empty state component
 */
const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
    <div className="w-16 h-16 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center mb-4">
      <svg
        className="w-8 h-8 text-neutral-400 dark:text-neutral-500"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
    </div>
    <h4 className="font-display font-semibold text-neutral-700 dark:text-neutral-300 mb-1">
      No recent activity
    </h4>
    <p className="text-sm text-neutral-500 dark:text-neutral-400">
      File transfer activity will appear here
    </p>
  </div>
);

/**
 * Single activity item component
 */
interface ActivityItemProps {
  activity: FileActivity;
  onClick?: (activity: FileActivity) => void;
  isNew?: boolean;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ activity, onClick, isNew = false }) => {
  // THUB-186: subscribe to the timezone context so this component RE-RENDERS when the
  // selector changes. The formatter shim above reads a module cache, which returns the
  // right value but cannot itself trigger a render -- without this the timestamp kept
  // showing the previous zone until a full page reload.
  useTimezone();
  // THUB-298: build the element here rather than aliasing the component type and rendering
  // <IconComponent/>. getEventIcon returns a component, and rendering a component produced by a
  // call during render is what react-hooks/static-components objects to -- React cannot rely on
  // the type being stable across renders. Same output, one indirection fewer.
  const renderIcon = (className: string) => {
    const Icon = getEventIcon(activity.eventType);
    return <Icon className={className} />;
  };
  const iconColorClasses = getIconColorClasses(activity.eventType);
  const iconBgClasses = getIconBgClasses(activity.eventType);
  const statusVariant = getStatusBadgeVariant(activity.status);

  const handleClick = () => {
    if (onClick) {
      onClick(activity);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const itemClasses = [
    'flex items-start gap-3 p-3 rounded-lg transition-colors duration-200',
    onClick ? 'cursor-pointer hover:bg-neutral-50 dark:hover:bg-neutral-800/50' : '',
    isNew ? 'animate-fade-in-up' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={itemClasses}
      onClick={handleClick}
      onKeyDown={onClick ? handleKeyDown : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={`${activity.eventType} event for ${activity.filename}`}
    >
      {/* Event type icon */}
      <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${iconBgClasses}`}>
        {renderIcon(`w-5 h-5 ${iconColorClasses}`)}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            {/* Filename */}
            <p
              className="font-body font-medium text-sm text-neutral-900 dark:text-white truncate"
              title={activity.filename}
            >
              {truncateFilename(activity.filename)}
            </p>

            {/* Partner and Task */}
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 truncate">
              {activity.partnerName} / {activity.taskName}
            </p>

            {/* Timestamp */}
            <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">
              {formatRelativeTime(activity.timestamp)}
            </p>

            {/* Error message if failed */}
            {activity.status === 'failed' && activity.errorMessage && (
              <p className="text-xs text-danger-600 dark:text-danger-400 mt-1 truncate" title={activity.errorMessage}>
                {activity.errorMessage}
              </p>
            )}
          </div>

          {/* Status badge */}
          <StatusBadge
            variant={statusVariant}
            size="sm"
            showDot={activity.status === 'processing'}
          >
            {getStatusLabel(activity.status)}
          </StatusBadge>
        </div>
      </div>
    </div>
  );
};

/**
 * RecentActivityFeed component
 *
 * Displays a scrollable feed of recent file transfer activity with event type icons,
 * filenames, partner/task information, relative timestamps, and status badges.
 */
export const RecentActivityFeed: React.FC<RecentActivityFeedProps> = ({
  activities,
  maxItems = 10,
  isLoading = false,
  onActivityClick,
  className = '',
}) => {
  // THUB-184: sort by timestamp descending (most recent first) before limiting.
  // The source list can arrive in DynamoDB scan/hash order (the initial file-audit
  // API load) or insertion order (live WebSocket prepends), so sorting here
  // guarantees the feed always surfaces the newest events first.
  const displayedActivities = [...activities]
    .sort((a, b) => {
      const ta = new Date(a.timestamp).getTime();
      const tb = new Date(b.timestamp).getTime();
      return (Number.isNaN(tb) ? 0 : tb) - (Number.isNaN(ta) ? 0 : ta);
    })
    .slice(0, maxItems);

  return (
    <Card className={className}>
      <CardHeader title="Recent Activity" subtitle="Latest file transfer events" />

      <CardBody padding="none">
        {/* Scrollable container */}
        <div
          className="max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-neutral-300 scrollbar-track-neutral-100 dark:scrollbar-thumb-neutral-600 dark:scrollbar-track-neutral-800"
          role="feed"
          aria-label="Recent file activity feed"
        >
          {isLoading ? (
            // Loading skeletons
            <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {Array.from({ length: 5 }).map((_, index) => (
                <ActivityItemSkeleton key={index} />
              ))}
            </div>
          ) : displayedActivities.length === 0 ? (
            // Empty state
            <EmptyState />
          ) : (
            // Activity list
            <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {displayedActivities.map((activity, index) => (
                <ActivityItem
                  key={activity.id}
                  activity={activity}
                  onClick={onActivityClick}
                  isNew={index === 0}
                />
              ))}
            </div>
          )}
        </div>
      </CardBody>
    </Card>
  );
};

export default RecentActivityFeed;
